// Headless status contract (fn-69.2, R2).
//
// The headless matter-intake kickoff (see `kickoffs.ts`) instructs the
// deployed `matter-creator` agent to emit, for EVERY natural-language
// turn, a single minified JSON object `{phase, title, detail?}` that the
// ArchIE headless UI renders as a shimmering status line.
//
// `phase`/`title`/`detail` are **model-authored = untrusted strings**.
// Consumers render them as plain text — NEVER as HTML/markdown (R2). The
// robustness budget lives in the parser (`parse-status.ts`), not the
// prompt: a non-conforming turn degrades to a neutral fallback status,
// never a thrown error and never raw JSON on screen.

/**
 * The machine-readable status the headless agent emits per turn.
 *
 *   - `phase`  — a snake_case token (e.g. `analyzing_document`,
 *     `creating_contact`, `done`). Stable-ish category for the UI.
 *   - `title`  — the shimmer heading (≤6 words by convention). The
 *     primary thing the UI renders.
 *   - `detail` — an optional one-clause elaboration.
 *
 * Untrusted: render as text only.
 */
export interface HeadlessStatus {
  readonly phase: string;
  readonly title: string;
  readonly detail?: string;
}

/**
 * The neutral status the UI shows when a turn cannot be parsed into a
 * `HeadlessStatus` (prose / garbage / oversized). Deliberately generic —
 * the UI may instead keep the last good line; this is the floor.
 */
export const NEUTRAL_FALLBACK_STATUS: HeadlessStatus = {
  phase: 'working',
  title: 'Working…',
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Structural validator for a candidate `HeadlessStatus`. Requires
 * non-empty `phase` + `title` strings; `detail`, when present, must be a
 * string (it may be empty — an empty elaboration is harmless). Extra
 * keys are tolerated: the model occasionally adds fields and we don't
 * want a stricter-than-necessary check to reject an otherwise-usable
 * status.
 */
export function isHeadlessStatus(value: unknown): value is HeadlessStatus {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const v = value as { phase?: unknown; title?: unknown; detail?: unknown };
  if (!isNonEmptyString(v.phase) || !isNonEmptyString(v.title)) {
    return false;
  }
  if (v.detail !== undefined && typeof v.detail !== 'string') {
    return false;
  }
  return true;
}

/**
 * Narrow a validated object to the canonical `HeadlessStatus` shape,
 * dropping any extra keys the model added so downstream consumers see a
 * clean object. Caller MUST have passed `isHeadlessStatus` first.
 */
export function normalizeHeadlessStatus(value: HeadlessStatus): HeadlessStatus {
  const out: { phase: string; title: string; detail?: string } = {
    phase: value.phase,
    title: value.title,
  };
  if (typeof value.detail === 'string') {
    out.detail = value.detail;
  }
  return out;
}
